import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'
import echarts from 'echarts/lib/echarts'
import DtoAnalysisUltraLight from 'quality/dto/analyse/DtoAnalysisUltraLight'
import i18n from 'simple-react-i18n'
import { Grid } from '@mui/material'
import useApplicationSetting, { listStringParser } from 'utils/customHook/useApplicationSetting'
import { round, minBy, orderBy, template, range, keyBy, groupBy, entries, max, compact, keys, sumBy, uniq } from 'lodash'
import { CATCHMENT_IRRELEVANT_PESTICIDES, CATCHMENT_LIST_SUM_PESTICIDES, CATCHMENT_PESTICIDES, NITRATES_CODE } from 'catchment/constants/CatchmentConstant'
import { exportPictureIcon, exportExcelIcon, operationIcon } from 'components/echart/EChartUtils'
import CatchmentDto from 'catchment/dto/CatchmentDto'
import CatchmentPointDto from 'catchment/dto/CatchmentPointDto'
import useStartPeriod from 'catchment/utils/useStartPeriod'
import useEndPeriod from 'catchment/utils/useEndPeriod'
import { getSettingBool } from 'utils/SettingUtils'
import { getDate } from 'utils/DateUtil'
import { getBeginingOfTheYear, getEndOfTheYear, getYear } from 'utils/DateUtil'
import { exportFile } from 'utils/ExportDataUtil'
import { grey, greyBlue, secondaryBlue, THRESHOLD_COLORS_CODES } from 'utils/constants/ColorTheme'
import { convertToRGBA } from 'utils/ColorUtil'
import { shallowEqual, useSelector } from 'react-redux'
import { DEFAULT_OBJECTIVE, DEFAULT_THRESHOLD_NITRATES, DEFAULT_THRESHOLD_PESTICIDES_1, DEFAULT_THRESHOLD_PESTICIDES_2 } from 'catchment/constants/CatchmentConstant'
import { QualityThresholdType } from 'quality/shapes/QualityThresholdShapes'
import { hasValue } from 'utils/NumberUtil'
import { REMARK_CODES } from 'utils/AnalyseUtils'

const NO_ANALYSIS_PERCENT = -1
const CATCHMENT_EXPORT_HEADERS = ['year', 'code', 'catchment']
const POINTS_EXPORT_HEADERS = ['year', 'code', 'catchmentPoint', 'cityCode', 'cityLabel']

const setYaxisTitle = (method) => {
    switch (method) {
        case 1 :
            return `${i18n.byCatchmentConformity} (%)`
        case 2 :
            return `${i18n.bySamplePointConformity} (%)`
        default :
            return `${i18n.catchmentsConformity} (%)`
    }
}

const setMethodColor = (method) => {
    switch (method) {
        case 1 :
            return greyBlue
        case 2 :
            return secondaryBlue
        default :
            return grey
    }
}

const labelFormatter = (nbYears) => {
    if (nbYears > 75) return 4
    if (nbYears > 50) return 3
    if (nbYears > 25) return 2
    return 1
}

const ObjectiveChart = ({
    analysis = [],
    objective = DEFAULT_OBJECTIVE,
    threshold,
    specificThresholds = {},
    catchmentsAndQualito = [],
    fileName = '',
    title = '',
    startPeriod,
    endPeriod,
}) => {
    const {
        cities,
    } = useSelector(store => ({
        cities: store.CityReducer.cities,
    }), shallowEqual)

    const [calculMethod, setCalculMethod] = useState(0)

    const calculMethodRef = useRef(calculMethod)

    useEffect(() => {
        calculMethodRef.current = calculMethod
    }, [calculMethod])

    const dates = useMemo(() => (range(getYear(startPeriod), getYear(endPeriod)+1)), [startPeriod, endPeriod])

    const serie = useMemo(() => (
        groupBy(dates.flatMap(d => {
            const yearFiltered = analysis.filter(({ sampleDate, result }) => hasValue(sampleDate) && hasValue(result) && getYear(sampleDate) === d)
            const catchmentsData = catchmentsAndQualito.flatMap(c => {
                const qualitoFiltered = yearFiltered.filter(a => c.qualito.includes(a.qualitometer))

                if (calculMethod !== 2) {
                    const totalAnalysisCatchment = qualitoFiltered.length
                    const totalOkCatchment = qualitoFiltered.filter(a => a.result < (specificThresholds[a.parameter]?.threshold1 || threshold)).length
                    const percentOkCatchment = totalAnalysisCatchment > 0 ? totalOkCatchment / totalAnalysisCatchment * 100 : NO_ANALYSIS_PERCENT
                    return {
                        name: c.name,
                        code: c.code,
                        data: qualitoFiltered,
                        value: round(percentOkCatchment, 5),
                        isCatchmentOk: percentOkCatchment >= objective,
                        symbol: totalAnalysisCatchment === 0 ? 'none' : 'circle',
                        symbolSize: 6,
                        date: getBeginingOfTheYear(d),
                    }
                }

                return c.points.flatMap(p => {
                    const qualitometer = p.qualito?.[0] || null
                    const city = qualitometer && cities.find(v => v.code === qualitometer.townCode)
                    const pointFiltered = qualitoFiltered.filter(a => p.qualito.map(q => q.id).includes(a.qualitometer))
                    const totalAnalysisPoint = pointFiltered.length
                    const totalOkPoint = pointFiltered.filter(a => a.result < (specificThresholds[a.parameter]?.threshold1 || threshold)).length
                    const percentOkPoint = totalAnalysisPoint > 0 ? totalOkPoint / totalAnalysisPoint * 100 : NO_ANALYSIS_PERCENT
                    return {
                        date: getBeginingOfTheYear(d),
                        name: `[${p.codeWithoutDesignation}]`,
                        code: p.codeWithoutDesignation,
                        qualitoName: qualitometer?.name,
                        cityName: city?.name,
                        cityCode: qualitometer?.townCode,
                        tooltipName: `${p.codeWithoutDesignation ? `[${p.codeWithoutDesignation}]` : ''} ${qualitometer?.name} ${city?.name ? `- ${city.name}` : ''}`,
                        mainPoint: p.mainPoint,
                        data: pointFiltered,
                        value: round(percentOkPoint, 5),
                        symbol: percentOkPoint === NO_ANALYSIS_PERCENT ? 'none' : 'circle',
                        symbolSize: 6,
                    }
                })
            })
            if (calculMethod !== 0) return catchmentsData

            const dataCatchments = catchmentsData.filter(c => c.value !== NO_ANALYSIS_PERCENT)
            const totalCatchments = dataCatchments.length
            const totalOk = dataCatchments.filter(c => c.isCatchmentOk).length
            const percentOk = dataCatchments.length > 0 ? totalOk / totalCatchments * 100 : 0
            return {
                date: getBeginingOfTheYear(d),
                data: catchmentsData,
                value: percentOk,
                symbol: dataCatchments.length === 0 ? 'none' : 'circle',
                symbolSize: 6,
                name: i18n.indicator,
            }
        }), 'name')
    ), [dates, analysis, catchmentsAndQualito, threshold, objective, specificThresholds, calculMethod, cities])

    const serieRef = useRef(serie)

    useEffect(() => {
        serieRef.current = serie
    }, [serie])

    const pointsColor = useMemo(() => calculMethod !== 0 ? {} : { itemStyle: { color: 'black' } }, [calculMethod])
    const labelInterval = useMemo(() => labelFormatter(dates.length), [dates.length])

    const series = useMemo(() => {
        return [...entries(serie).map(s => {
            return {
                type: 'line',
                name: s[0],
                data: s[1],
                ... pointsColor,
                lineStyle: { opacity: 0 },
                animationDuration: 500,
            }
        }),
        {
            type: 'line',
            name: 'Objectif non atteint',
            data: [],
            itemStyle: { color: convertToRGBA(THRESHOLD_COLORS_CODES.RED, 0.2) },
            lineStyle: { opacity: 0 },
            showSymbol: false,
            isArea: true,
        },
        {
            type: 'line',
            name: 'Objectif atteint',
            data: [],
            itemStyle: { color: convertToRGBA(THRESHOLD_COLORS_CODES.GREEN, 0.2) },
            lineStyle: { opacity: 0 },
            showSymbol: false,
            isArea: true,
            markArea: {
                data: [
                    [
                        { yAxis: 0, itemStyle: {
                            color: THRESHOLD_COLORS_CODES.RED,
                            opacity: 0.2,
                        } },
                        { yAxis: objective },
                    ],
                    [
                        { yAxis: objective, itemStyle: {
                            color: THRESHOLD_COLORS_CODES.GREEN,
                            opacity: 0.2,
                        } },
                        { yAxis: 100 },
                    ],
                ],
            },
        },
        ]
    }, [serie, objective, pointsColor])

    const options = {
        title: {
            text: title,
            left: 'center',
        },
        series,
        xAxis: [{
            type: 'category',
            data: dates,
            boundaryGap: true,
            showSplitLine: true,
            axisLabel: {
                rotate: 50,
                interval: 0,
                formatter: (value, index) => index % labelInterval === 0 ? value : undefined,
            },
        }],
        yAxis: [{
            type: 'value',
            position: 'left',
            boundaryGap: true,
            min: 0,
            max: 100,
            name: setYaxisTitle(calculMethod),
            nameLocation: 'center',
            nameGap: 50,
        }],
        legend: {
            top: '10%',
            left: '10%',
            right: 120,
            type: 'scroll',
            data: series.map(s => ({ name: s.name, icon: s.isArea ? 'roundRect' : null })),
        },
        grid: {
            top: '20%',
            left: '6%',
            right: '3%',
            bottom: '20',
            containLabel: true,
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow',
                snap: true,
            },
            formatter: params => {
                const hasData = params.length > 0
                if (!hasData) return undefined
                const areEmpty = params.filter(p => p.data?.data?.length > 0).length === 0
                if (areEmpty) return undefined

                const pointData = params.length > 0 ? params[0] : { data: {} }
                const yearData = pointData.data ?? { data: {} }
                const date = yearData.date ? getDate(yearData.date, 'YYYY') : i18n.noYear

                if (params.length === 1 && params[0].seriesName === i18n.indicator) {
                    const catchments = yearData.data ? yearData.data : []
                    const catchmentsNumber = catchments.length ?? 0
                    if (catchmentsNumber === 0) return undefined
                    const catchmentsWithData = catchments.filter(c => c.value !== NO_ANALYSIS_PERCENT)
                    if (catchmentsWithData.length === 0) return undefined
                    const okCatchments = catchmentsWithData.filter(c => c.isCatchmentOk)
                    return `${i18n.year} ${date} <br/> ${template(i18n.annualCatchmentData)({ okCatchments: okCatchments.length, koCatchments: catchmentsWithData.length - okCatchments.length, noDataCatchments: catchmentsNumber - catchmentsWithData.length })}`
                }

                params.sort((a, b) => {
                    if (a?.data?.value === undefined) return 1
                    if (b?.data?.value === undefined) return -1
                    return b?.data?.value - a?.data?.value
                })

                return params.reduce((acc, act) => {
                    if (calculMethod === 1) return act.data?.symbol === 'none' ? acc : `${acc}<br>${act.data?.code ? `[${act.data?.code}]` : ''} ${act.data?.name} : ${act.data?.value}%`
                    return act.data?.symbol === 'none' ? acc : `${acc}<br>${act.data?.tooltipName || act.data?.name} : ${act.data?.value}%`
                }, `${i18n.year} ${date}`)
            },
        },
        toolbox: {
            right: '3%',
            top: '10%',
            feature: {
                myToolNamed: {
                    title: i18n.changeCalcMethod,
                    icon: operationIcon,
                    onclick: () => setCalculMethod(prev => (prev + 1) % 3),
                    iconStyle: {
                        borderColor: setMethodColor(calculMethod),
                    },
                },
                saveAsImage: {
                    title: i18n.export,
                    icon: exportPictureIcon,
                    name: fileName,
                },
                myToolExport: {
                    title: i18n.excelExport,
                    icon: exportExcelIcon,
                    onclick: () => {
                        const exportData = compact(entries(serieRef.current).flatMap(s => {
                            const val = s[1]
                            return val.flatMap(v => {
                                if (calculMethodRef.current === 0) {
                                    return v.data.map(c => {
                                        const catchmentValue = c.value
                                        if (catchmentValue === NO_ANALYSIS_PERCENT) return undefined

                                        const catchmentName = c.name
                                        const catchmentCode = c.code
                                        const catchmentData = c.data

                                        const totalAnalysis = catchmentData?.length
                                        const percentOk = max([catchmentValue, 0])
                                        const percentKo = totalAnalysis > 0 ? 100 - percentOk : 0

                                        return {
                                            year: { value: getDate(v.date, 'YYYY'), cellType: 'right' },
                                            catchment: catchmentName,
                                            code: catchmentCode,
                                            conformity: { value: percentOk >= objective ? i18n.yes : i18n.no },
                                            totalAnalysis: { value: totalAnalysis, format: '0', cellType: 'number' },
                                            percentOk: { value: `${round(percentOk, 2)}%`, cellType: 'right' },
                                            percentKo: { value: `${round(percentKo, 2)}%`, cellType: 'right' },
                                        }
                                    })
                                }

                                const totalAnalysis = v.data?.length
                                const percentOk = max([v.value, 0])
                                const percentKo = totalAnalysis > 0 ? 100 - percentOk : 0

                                const commonReturn = {
                                    conformity: { value: percentOk >= objective ? i18n.yes : i18n.no },
                                    totalAnalysis: { value: totalAnalysis, format: '0', cellType: 'number' },
                                    percentOk: { value: `${round(percentOk, 2)}%`, cellType: 'right' },
                                    percentKo: { value: `${round(percentKo, 2)}%`, cellType: 'right' },
                                }

                                return calculMethodRef.current !== 2 ? {
                                    year: { value: getDate(v.date, 'YYYY'), cellType: 'right' },
                                    sortYear: getDate(v.date, 'YYYY'),
                                    catchment: v.name,
                                    code: { value: v.code, cellType: 'left' },
                                    ... commonReturn,
                                } : {
                                    year: { value: getDate(v.date, 'YYYY'), cellType: 'right' },
                                    sortYear: getDate(v.date, 'YYYY'),
                                    code: { value: v.code, cellType: 'left' },
                                    catchmentPoint: v.qualitoName,
                                    cityCode: v.cityCode,
                                    cityLabel: v.cityName,
                                    ... commonReturn,
                                }
                            })
                        }))

                        const sortedData = orderBy(exportData, ['sortYear', 'year'], ['asc'])
                        const specificColumns = calculMethodRef.current === 2 ? POINTS_EXPORT_HEADERS : CATCHMENT_EXPORT_HEADERS
                        exportFile({
                            data: exportData.length ? [
                                {
                                    ...sortedData[0],
                                    headers: [...specificColumns, 'conformity', 'totalAnalysis', 'percentOk', 'percentKo'],
                                    customHeaders: [...specificColumns, 'conformity', 'nbQuantifications', 'complianceProportion', 'nonComplianceProportion'],
                                },
                                ...sortedData.slice(1),
                            ] : [],
                            exportType: 'xlsx',
                            titleFile: fileName,
                        }, true)
                    },
                },
            },
        },
    }

    return (
        <ReactECharts
            echarts={echarts}
            option={options}
            notMerge
            lazyUpdate
            style={{ height: 400 }}
        />
    )
}

ObjectiveChart.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.instanceOf(DtoAnalysisUltraLight),
        PropTypes.shape({
            result: PropTypes.number,
            sampleDate: PropTypes.number,
            qualitometer: PropTypes.number,
            parameter: PropTypes.string,
        }),
    ])),
    objective: PropTypes.number,
    threshold: PropTypes.number,
    specificThresholds: PropTypes.objectOf(QualityThresholdType),
    catchmentsAndQualito: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        code: PropTypes.string,
        points: PropTypes.arrayOf(PropTypes.shape({
            codeWithoutDesignation: PropTypes.string,
            mainPoint: PropTypes.bool,
            qualito: PropTypes.arrayOf(PropTypes.shape({
                id: PropTypes.number,
                townCode: PropTypes.string,
                name: PropTypes.string })),
        })),
        qualito: PropTypes.arrayOf(PropTypes.number),
    })),
    areaColor: PropTypes.arrayOf(PropTypes.string),
    fileName: PropTypes.string,
    title: PropTypes.string,
    unit: PropTypes.string,
    startPeriod: PropTypes.number,
    endPeriod: PropTypes.number,
}

const ObjectivesChartNitrates = ({
    catchments = [],
    catchmentPoints = [],
    analysis = [],
}) => {
    const {
        settings,
        qualitometers,
    } = useSelector(store => ({
        settings: store.AdministrationReducer.applicationSettings,
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const startPeriod = useStartPeriod(getBeginingOfTheYear)
    const endPeriod = useEndPeriod(getEndOfTheYear)

    const objectiveNitrates = useApplicationSetting('NITRATES_OBJECTIVE', setting => setting ? parseFloat(setting) : DEFAULT_OBJECTIVE)
    const nitratesThreshold = useApplicationSetting('CATCHMENT_NITRATES_THRESHOLD', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_NITRATES)
    const groupedNitratesAnalysis = useMemo(() => (analysis.filter(({ parameter }) => parameter === NITRATES_CODE).filter(a => a.remark === REMARK_CODES.VALID)), [analysis])

    const calcMinYear = useCallback((dataToCheck) => getDate(minBy(dataToCheck, 'sampleDate')?.sampleDate, 'YYYY'), [])

    const firstCatchmentYear = getSettingBool(settings, 'FIRST_CATCHMENT_YEAR')
    const newNitratesStartPeriod = useMemo(() => {
        const calculatedStartPeriod = firstCatchmentYear ? calcMinYear(groupedNitratesAnalysis) : undefined
        return calculatedStartPeriod > startPeriod ? calculatedStartPeriod : startPeriod
    }, [calcMinYear, firstCatchmentYear, groupedNitratesAnalysis, startPeriod])

    const catchmentsAndPointsReducted = useMemo(() => (
        catchments.map(c => {
            const points = catchmentPoints.filter(p => p.id === c.id).map(p => ({
                codeWithoutDesignation: p.codeWithoutDesignation,
                mainPoint: p.mainPoint ?? false,
            }))
            return {
                id: c.id,
                name: c.name,
                code: c.code,
                points,
            }
        })
    ), [catchments, catchmentPoints])

    const catchmentsAndQualitoReducted = useMemo(() => (
        catchmentsAndPointsReducted.map(c => ({
            id: c.id,
            name: c.name,
            code: c.code,
            points: c.points.map(p => ({
                codeWithoutDesignation: p.codeWithoutDesignation,
                mainPoint: p.mainPoint,
                qualito: qualitometers.filter(q => q.code === p.codeWithoutDesignation).map(q => ({ id: q.id, townCode: q.townCode, name: q.name })),
            })),
            qualito: c.points.flatMap(p => qualitometers.filter(q => q.code === p.codeWithoutDesignation)).map(q => q.id),
        }))
    ), [catchmentsAndPointsReducted, qualitometers])

    return (
        <ObjectiveChart
            analysis={groupedNitratesAnalysis}
            objective={objectiveNitrates}
            threshold={nitratesThreshold}
            catchmentsAndQualito={catchmentsAndQualitoReducted}
            fileName={i18n.annualNitratesObjective}
            title={template(i18n.annualNitratesObjectiveDetails)({ objective: objectiveNitrates, threshold: nitratesThreshold })}
            startPeriod={newNitratesStartPeriod}
            endPeriod={endPeriod}
        />
    )
}

ObjectivesChartNitrates.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.instanceOf(CatchmentPointDto),
        PropTypes.shape({
            mainPoint: PropTypes.bool,
        }),
    ])),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
}

const ObjectivesChartPesticides = ({
    catchments = [],
    catchmentPoints = [],
    analysis = [],
    specificThresholds = [],
}) => {
    const {
        settings,
        qualitometers,
    } = useSelector(store => ({
        settings: store.AdministrationReducer.applicationSettings,
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const startPeriod = useStartPeriod(getBeginingOfTheYear)
    const endPeriod = useEndPeriod(getEndOfTheYear)

    const objectiveSumPesticides = useApplicationSetting('PESTICIDES_SUM_OBJECTIVE', setting => setting ? parseFloat(setting) : DEFAULT_OBJECTIVE)
    const objectivePesticides = useApplicationSetting('PESTICIDES_OBJECTIVE', setting => setting ? parseFloat(setting) : DEFAULT_OBJECTIVE)

    const pesticideThreshold1 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_1', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_1)
    const pesticideThreshold2 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_2', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_2)

    const relevantPesticides = useApplicationSetting(CATCHMENT_PESTICIDES, listStringParser)
    const irrelevantPesticides = useApplicationSetting(CATCHMENT_IRRELEVANT_PESTICIDES, listStringParser)
    const pesticides = useMemo(() => uniq([...relevantPesticides, ...irrelevantPesticides]), [relevantPesticides, irrelevantPesticides])
    const listSumPesticides = useApplicationSetting(CATCHMENT_LIST_SUM_PESTICIDES, listStringParser)

    const thresholdsIndexed = useMemo(() => keyBy(specificThresholds ?? [], 'parameterCode'), [specificThresholds])

    const pesticidesAnalysis = useMemo(() => (analysis.filter(({ parameter }) => parameter !== NITRATES_CODE).filter(a => a.remark === REMARK_CODES.VALID)), [analysis])
    const groupPesticidesAnalysis = useMemo(() => (
        pesticides.length ? pesticidesAnalysis.filter(a => pesticides.includes(a.parameter)) : pesticidesAnalysis
    ), [pesticides, pesticidesAnalysis])
    const groupSumPesticidesAnalysis = useMemo(() => (
        listSumPesticides.length ? pesticidesAnalysis.filter(a => listSumPesticides.includes(a.parameter)) : groupPesticidesAnalysis
    ), [listSumPesticides, pesticidesAnalysis, groupPesticidesAnalysis])

    const sumAnalysis = useMemo(() => {
        const groupByOperation = groupBy(groupSumPesticidesAnalysis, a => `${a.qualitometer}#${a.operation}`)
        return keys(groupByOperation).map(key => {
            const { [key]: randomName = [] } = groupByOperation
            return {
                result: sumBy(randomName, 'result'),
                sampleDate: randomName[0].sampleDate,
                qualitometer: randomName[0].qualitometer,
                parameter: randomName[0].parameter,
            }
        })
    }, [groupSumPesticidesAnalysis])

    const calcMinYear = useCallback((dataToCheck) => getDate(minBy(dataToCheck, 'sampleDate')?.sampleDate, 'YYYY'), [])

    const firstCatchmentYear = getSettingBool(settings, 'FIRST_CATCHMENT_YEAR')
    const newSumPesticidesStartPeriod = useMemo(() => {
        const calculatedStartPeriod = firstCatchmentYear ? calcMinYear(sumAnalysis) : undefined
        return calculatedStartPeriod > startPeriod ? calculatedStartPeriod : startPeriod
    }, [calcMinYear, firstCatchmentYear, sumAnalysis, startPeriod])
    const newPesticidesStartPeriod = useMemo(() => {
        const calculatedStartPeriod = firstCatchmentYear ? calcMinYear(groupPesticidesAnalysis) : undefined
        return calculatedStartPeriod > startPeriod ? calculatedStartPeriod : startPeriod
    }, [calcMinYear, firstCatchmentYear, groupPesticidesAnalysis, startPeriod])

    const catchmentsAndPointsReducted = useMemo(() => (
        catchments.map(c => {
            const points = catchmentPoints.filter(p => p.id === c.id).map(p => ({
                codeWithoutDesignation: p.codeWithoutDesignation,
                mainPoint: p.mainPoint ?? false,
            }))
            return {
                id: c.id,
                name: c.name,
                code: c.code,
                points,
            }
        })
    ), [catchments, catchmentPoints])

    const catchmentsAndQualitoReducted = useMemo(() => (
        catchmentsAndPointsReducted.map(c => ({
            id: c.id,
            name: c.name,
            code: c.code,
            points: c.points.map(p => ({
                codeWithoutDesignation: p.codeWithoutDesignation,
                mainPoint: p.mainPoint,
                qualito: qualitometers.filter(q => q.code === p.codeWithoutDesignation).map(q => ({ id: q.id, townCode: q.townCode, name: q.name })),
            })),
            qualito: c.points.flatMap(p => qualitometers.filter(q => q.code === p.codeWithoutDesignation).map(q => q.id)),
        }))
    ), [catchmentsAndPointsReducted, qualitometers])

    return (
        <Grid container>
            <Grid size={6}>
                <ObjectiveChart
                    analysis={sumAnalysis}
                    objective={objectiveSumPesticides}
                    threshold={pesticideThreshold2}
                    catchmentsAndQualito={catchmentsAndQualitoReducted}
                    fileName={i18n.annualSumPesticidesObjective}
                    title={template(i18n.annualSumPesticidesObjectiveDetails)({ objective: objectiveSumPesticides, threshold: pesticideThreshold2 })}
                    startPeriod={newSumPesticidesStartPeriod}
                    endPeriod={endPeriod}
                />
            </Grid>
            <Grid size={6}>
                <ObjectiveChart
                    analysis={groupPesticidesAnalysis}
                    objective={objectivePesticides}
                    threshold={pesticideThreshold1}
                    specificThresholds={thresholdsIndexed}
                    catchmentsAndQualito={catchmentsAndQualitoReducted}
                    fileName={i18n.annualPesticidesObjective}
                    title={template(i18n.annualPesticidesObjectiveDetails)({ objective: objectivePesticides, threshold: pesticideThreshold1 })}
                    startPeriod={newPesticidesStartPeriod}
                    endPeriod={endPeriod}
                />
            </Grid>
        </Grid>
    )
}

ObjectivesChartPesticides.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.instanceOf(CatchmentPointDto),
        PropTypes.shape({
            mainPoint: PropTypes.bool,
        }),
    ])),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
    specificThresholds: PropTypes.arrayOf(QualityThresholdType),
}


export { ObjectivesChartNitrates, ObjectivesChartPesticides }